<template>
  <!-- Android Chrome: show install banner when browser offers installation -->
  <!-- canInstall is true only after beforeinstallprompt fires (requires SW + manifest + HTTPS) -->
  <div
    v-if="!dismissed && canInstallPwa && canInstall && !isIOS && !isStandalone"
    class="install-banner"
  >
    <div class="install-banner__content">
      <p class="install-banner__text">Diese App auf dem Homescreen installieren?</p>
      <div class="install-banner__actions">
        <button class="install-banner__btn install-banner__btn--primary" @click="handleInstall">
          Installieren
        </button>
        <button class="install-banner__btn install-banner__btn--secondary" @click="dismiss">
          Nicht jetzt
        </button>
      </div>
    </div>
  </div>

  <!-- iOS Safari: show step-by-step guide when in browser (not standalone) -->
  <!-- navigator.standalone === false means iOS browser (not yet installed) -->
  <div v-else-if="!dismissed && canInstallPwa && isIOS && !isStandalone" class="ios-install-guide">
    <div class="ios-install-guide__content">
      <p class="ios-install-guide__title">
        <strong>App installieren (iOS)</strong>
      </p>
      <ol class="ios-install-guide__steps">
        <li>
          Tippe auf das <strong>Teilen-Symbol</strong>
          <span class="ios-install-guide__icon" aria-hidden="true">&#x2B06;</span>
          in der Safari-Menüleiste
        </li>
        <li>Wähle <strong>„Zum Home-Bildschirm"</strong></li>
        <li>Bestätige mit <strong>„Hinzufügen"</strong></li>
      </ol>
      <button class="ios-install-guide__close" @click="dismiss">Schliessen</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useInstallPrompt } from '@/composables/useInstallPrompt';

const store = useStore();
const { canInstall, isIOS, isStandalone, triggerInstall } = useInstallPrompt();
const dismissed = ref(false);

const canInstallPwa = computed(() => store.getters.isFeatureEnabled('pwa_install'));

function dismiss() {
  dismissed.value = true;
}

async function handleInstall() {
  const outcome = await triggerInstall();
  if (outcome === 'dismissed') {
    // User declined — hide banner (don't re-show in this session)
    dismissed.value = true;
  }
  // If 'accepted', canInstall becomes false via the composable (appinstalled fires)
}
</script>

<style scoped>
.install-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 2px solid #2d6a4f;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.12);
  padding: 1rem;
  z-index: 9999;
}

.install-banner__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

.install-banner__text {
  margin: 0;
  font-size: 0.95rem;
  color: #333;
}

.install-banner__actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.install-banner__btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.install-banner__btn--primary {
  background: #2d6a4f;
  color: white;
}

.install-banner__btn--secondary {
  background: #e9ecef;
  color: #333;
}

.ios-install-guide {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 2px solid #2d6a4f;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.12);
  padding: 1rem;
  z-index: 9999;
}

.ios-install-guide__content {
  max-width: 600px;
  margin: 0 auto;
}

.ios-install-guide__title {
  margin: 0 0 0.75rem;
}

.ios-install-guide__steps {
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #444;
}

.ios-install-guide__icon {
  font-size: 1.1em;
  vertical-align: middle;
  margin: 0 0.1rem;
}

.ios-install-guide__close {
  padding: 0.5rem 1rem;
  background: #e9ecef;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}
</style>
